import * as React from "react"
import { useQuery, useMutation, useLazyQuery } from "@apollo/client"
import { ErrorBoundary } from "react-error-boundary"
import { useRecoilState, useRecoilValue } from "recoil"
import {
  ConnectProjectRegion,
  CoordinateSystemUnit,
  CreateProjectRequest,
  ProjectCreatedWithStatus,
  Point
} from "../../types"
import { GET_CONFIG, HAS_ACCESS, CREATE_PROJECT, SYNC_PROJECT, ELIGIBLE_ACCOUNTS } from "../../api/QuadriApi"
import ConnectApi, { ConnectData } from "../../api/ConnectApi"
import {
  addSnackSelector,
  extensionApiState,
  projectIdState,
  projectOriginState,
  projectRegionState,
  quadriProjectIdState,
  signedInUserState
} from "../StateSelectors"
import { PendingSpinner } from "../Pending"
import { WarningBanner, WarningTypes } from "./WarningBanner"
import { OopsFallback } from "../Oops/OopsFallback"
import ImportedProjectSettings from "./ImportedProjectSettings"
import ConnectProjectContext from "./ConnectProjectContext"
import QuadriProjectSettings from "./QuadriProjectSettings"
import QuadriHeader from "./QuadriHeader"
import QuadriFooter from "./QuadriFooter"
import { getMenu } from "../../util/getMenu"
import { useTranslation } from "react-i18next"
import { exceptionFromApolloError } from "../../util/apolloError"
import MissingLicense from "../MissingLicense"
import { SnackProps } from "./SnackBars"
import QuadriOwnershipSettings from "./QuadriOwnershipSettings"
import ProjectSizeInfo from "./ProjectSizeInfo"
import { track } from "../../util/gainsight"

const BACKUP_RELOAD_WARNING = 10000
const BACKUP_RELOAD_TIMEOUT = 15000

// Used to determine the config page state. Exported so that the progress banner can use it
export type ConfigStatus = "Creating" | "Active" | "Failed" | "Updating" | "Default" | "Deleting"

// The exported wrapper component that handles error boundary and input
// to the loading component
const ConfigMain = () => {
  const projectId = useRecoilValue(projectIdState)
  const projectOrigin = useRecoilValue(projectOriginState)

  return (
    <ErrorBoundary FallbackComponent={OopsFallback}>
      <ConfigLoad {...{ projectId, projectOrigin }} />
    </ErrorBoundary>
  )
}

interface ConfigLoadProps {
  projectId?: string
  projectOrigin?: string
  accessToken?: string
}

// TODO: Shorten this somehow
// The responsibility of this is to load everything necessary to render the
// Config page. It must therefore also hold initial project state as well
// as data from Connect
const ConfigLoad = (props: ConfigLoadProps) => {
  const { projectId, projectOrigin } = props
  const { t } = useTranslation(["common", "config"])

  if (!projectId) throw new Error("Cannot load config page: Missing project id")
  if (!projectOrigin) throw new Error("Cannot load config page: Missing project origin")

  // Need to set this if this is the first time project is created
  const [, setQuadriProjectId] = useRecoilState(quadriProjectIdState)
  const extensionApi = useRecoilValue(extensionApiState)
  const [, setProjectRegionState] = useRecoilState(projectRegionState)
  const [projectStatus, setProjectStatus] = React.useState(undefined as ConfigStatus | undefined)

  // Connect Data
  const [connectApi, setConnectApi] = React.useState<ConnectApi | undefined>(undefined)
  const [connectData, setConnectData] = React.useState<ConnectData>({})
  const [fetchedConnect, setFetchedConnect] = React.useState(false)
  const [ownershipAccount, setOwnershipAccount] = React.useState("")
  const signedInUser = useRecoilValue(signedInUserState)

  // Feature Catalogs data depends on if the project has ever been created...
  const accessQuery = useQuery(HAS_ACCESS)

  const accountsQuery = useQuery(ELIGIBLE_ACCOUNTS)

  const projectQuery = useQuery(GET_CONFIG, {
    variables: { id: projectId },
    onCompleted: (data) => {
      const updatedStatus = data?.tryGetConnectQuadriProject?.project?.projectStatus
      if (typeof updatedStatus !== "undefined") {
        setProjectStatus(updatedStatus)
        console.log("Setting project status: ", updatedStatus)
      }

      const quadriProjectId = data?.tryGetConnectQuadriProject?.project?.quadriProjectId
      if (quadriProjectId) {
        setQuadriProjectId(quadriProjectId)
        console.log("Set Quadri Project Id: ", quadriProjectId)
      }
    }
  })

  const { data } = projectQuery

  React.useEffect(() => {
    const runEffectAsync = async () => {
      const api = new ConnectApi(projectOrigin)
      setConnectApi(api)
      const connectData = await api.getConnectProjectData(projectId)
      console.log("Connect data: ", connectData)
      setConnectData(connectData)
      setOwnershipAccount(connectData.connectProject?.license?.accounts[0]?.name)
      setProjectRegionState(connectData.region)
      setFetchedConnect(true)
    }
    runEffectAsync()
  }, [projectOrigin, projectId, setProjectRegionState])

  // Show the menu if the project is returned as active
  React.useEffect(() => {
    if (projectStatus === "Active") {
      console.log("Showing extension menu items")
      console.log("GET CONFIG query data: ", data)
      extensionApi?.ui.setMenu(getMenu(t))
    }
  }, [extensionApi?.ui, projectStatus, data, t])

  // "Handle" errors (they are handled by parent error boundary)
  if (accessQuery.error) throw exceptionFromApolloError(accessQuery.error)
  if (accountsQuery.error) throw exceptionFromApolloError(accountsQuery.error)
  if (projectQuery.error) throw exceptionFromApolloError(projectQuery.error)

  // From here, the queries are either loading, or they have data
  if (!connectApi || !fetchedConnect || accessQuery.loading || accountsQuery.loading || projectQuery.loading) {
    return <PendingSpinner />
  } else {
    // Here, everything loaded successfully

    const needsLicense = !accessQuery.data.hasAccess?.access
    const noAccounts = !accountsQuery.data.eligibleAccountsWithLicenses

    // // Uncomment to skip license checking:
    // const needsLicense = false
    // const noAccounts = false

    if (needsLicense) {
      return (
        <MissingLicense
          heading={t("licenseError.heading", { ns: "config" })}
          body1={t("licenseError.body1", { ns: "config" })}
          body2={t("licenseError.body2", { ns: "config" })}
          moreInfo={t("forMoreInfo.moreInfo", { ns: "common" })}
          quadriPlans={t("forMoreInfo.quadriPlans", { ns: "common" })}
          info={signedInUser}
          andCheck={t("forMoreInfo.andCheck", { ns: "common" })}
          subscriptions={t("forMoreInfo.subscriptions", { ns: "common" })}
        ></MissingLicense>
      )
    }
    if (noAccounts) {
      return (
        <MissingLicense
          heading={t("customerNotEligible.heading", { ns: "config" })}
          body1={t("customerNotEligible.body1", { ns: "config" })}
          body2={t("customerNotEligible.body2", { ns: "config" })}
          moreInfo={t("forMoreInfo.moreInfo", { ns: "common" })}
          quadriPlans={t("forMoreInfo.quadriPlans", { ns: "common" })}
          info={ownershipAccount}
          andCheck={t("forMoreInfo.andCheck", { ns: "common" })}
          subscriptions={t("forMoreInfo.subscriptions", { ns: "common" })}
        ></MissingLicense>
      )
    }

    // But do we have a project???
    const { found, project } = projectQuery.data.tryGetConnectQuadriProject

    const innerProps = {
      connectProjectId: projectId,
      featureCatalogId: project?.featureCatalogId,
      extensionOwnershipId: project?.customerAccountId,
      extensionOwnershipName: project?.customerAccountName,
      extensionLicenseId: project?.productId,
      coordinateSystemUnit: project?.coordinateSystemUnit,
      coordinateSystemName: project?.coordinateSystemName,
      projectFound: found,
      connectData,
      noAccounts,
      projectStatus,
      projectStatusMessage: project?.projectStatusMessage,
      projectUpdateMessage: project?.projectUpdateMessage,
      createdAt: project?.createdAt,
      updatedAt: project?.updatedAt,
      permissionsUpdatedAt: project?.permissionsUpdatedAt,
      projectCreatedWithStatus: project?.projectCreatedWithStatus,
      tcpsArea: project?.projectArea,
      setProjectStatus,
      setQuadriProjectId
    }
    return <ConfigMainInner {...innerProps} />
  }
}

interface ConfigMainInnerProps {
  connectProjectId: string
  connectData: ConnectData
  featureCatalogId?: string
  extensionOwnershipId: string
  extensionOwnershipName: string
  extensionLicenseId: string
  coordinateSystemUnit?: CoordinateSystemUnit
  coordinateSystemName?: string
  projectFound?: boolean
  projectStatus?: ConfigStatus
  projectStatusMessage?: string
  projectUpdateMessage?: string
  createdAt?: string
  updatedAt?: string
  permissionsUpdatedAt?: string
  projectCreatedWithStatus?: ProjectCreatedWithStatus
  tcpsArea?: Point[]
  setProjectStatus: (status: ConfigStatus) => void
  setQuadriProjectId: (quadriProjectId: string) => void
}

// This is the inner component responsible for page interaction and rendering
// It is assumed that all required data is loaded when this is rendered
const ConfigMainInner = (props: ConfigMainInnerProps) => {
  const { region, unitSystem, boundaryCoordinates, crsName, connectProject } = props.connectData
  const { projectStatus, projectStatusMessage, createdAt, updatedAt, permissionsUpdatedAt, connectProjectId } = props
  const { setProjectStatus, setQuadriProjectId } = props
  const { projectUpdateMessage, projectCreatedWithStatus } = props
  const [, addSnack] = useRecoilState(addSnackSelector)
  const { t } = useTranslation("config")

  // Feature catalog state is kept here and not in child QuadriProjectSettings component
  // because it is used in the creation request that is tied to the button.
  const [extensionOwnership, setExtensionOwnership] = React.useState<string | undefined>(props.extensionOwnershipId)
  const [featureCatalogId, setFeatureCatalogId] = React.useState<string | undefined>(props.featureCatalogId)
  const [featureCatalogStandard, setFeatureCatalogStandard] = React.useState<string | undefined>(undefined)
  const [coordinateSystemUnit, setCoordinateSystemUnit] = React.useState<CoordinateSystemUnit | undefined>(
    props.coordinateSystemUnit
  )

  // Save the id of the existing feature catalog if there is one. This is used
  // to fetch a list of compatible feature catalogs if the user wants to upgrade
  const existingFcId = props.featureCatalogId || null

  // If project is never created, then this is the mutation that gets sent on button click
  const [createProject, createProjectResponse] = useMutation(CREATE_PROJECT, {
    onCompleted: (data: any) => {
      console.log("On create complete, data: ", data)
      const quadriProjectId = data?.createProject?.projectId
      if (typeof quadriProjectId !== "undefined") {
        setQuadriProjectId(quadriProjectId)
      }
      startBackupTimeoutReload(t, addSnack)
    },
    onError: (_error) => setProjectStatus("Failed")
  })

  // Mutation used for syncing after the project has been created
  const [syncProject, syncProjectResponse] = useMutation(SYNC_PROJECT, {
    onCompleted: (data: any) => startBackupTimeoutReload(t, addSnack)
  })

  // Callback function to Create/Sync button
  const submitQuadriProject = (e: any) => {
    e.preventDefault()
    if (
      connectProjectId !== undefined &&
      featureCatalogId !== undefined &&
      coordinateSystemUnit !== undefined &&
      region !== undefined &&
      extensionOwnership !== undefined
    ) {
      const project = {
        connectProjectId: connectProjectId,
        featureCatalogId,
        coordinateSystemUnit,
        connectProjectRegion: region,
        customerAccountId: extensionOwnership
      }

      if (projectStatus === "Active") {
        // Sync project request is the same as create project, but without customerAccountId
        // since it cannot be changed after creation
        track("project_update", { connectProjectId: connectProjectId })
        const syncProjectRequest = { ...project } as any
        delete syncProjectRequest.customerAccountId
        setProjectStatus("Updating")
        syncProject({ variables: { project: syncProjectRequest } })
        console.log("Updating")
      } else {
        track("project_create", { connectProjectId: connectProjectId })
        setProjectStatus("Creating")
        createProject({ variables: { project: project } })
        console.log("Creating")
      }
    } else {
      console.error("Missing create/update parameters")
    }
  }

  // Get status of the config page, this is a combination of project status and query statuses
  // const configStatus = getConfigStatus(createProjectResponse, syncProjectResponse, projectStatus)
  const configStatus = projectStatus || "Default"

  // Boolean for if an asynchronous operation is in progress
  const changeInProgress = configStatus === "Creating" || configStatus === "Updating" || configStatus === "Deleting"

  // Disable creation
  const isEnableButtonDisabled = changeInProgress || !featureCatalogId || !coordinateSystemUnit || !extensionOwnership

  // See whether region is supported
  const regionSupported = region === ConnectProjectRegion.europe || region === ConnectProjectRegion.northAmerica

  // Disable editing the feature catalogs
  const disableEdit = changeInProgress || !regionSupported
  const disableEditOwnership = disableEdit || configStatus !== "Default"
  const quadriProjectId = useRecoilValue(quadriProjectIdState)

  return (
    <>
      {regionSupported ? null : <WarningBanner warningType={WarningTypes.Region} />}
      <QuadriHeader
        configStatus={configStatus}
        isEnableButtonDisabled={isEnableButtonDisabled}
        statusMessage={projectStatusMessage}
        updateErrorMessage={projectUpdateMessage}
        createProjectResponse={createProjectResponse}
        submitQuadriProject={submitQuadriProject}
        createdAt={createdAt}
        updatedAt={updatedAt}
        permissionsUpdatedAt={permissionsUpdatedAt}
        projectCreatedWithStatus={projectCreatedWithStatus}
      />
      <QuadriOwnershipSettings
        extensionOwnership={extensionOwnership}
        extensionOwnershipName={props.extensionOwnershipName}
        setExtensionOwnership={setExtensionOwnership}
        disableEdit={disableEditOwnership}
      />
      <QuadriProjectSettings
        existingFcId={existingFcId}
        fcStandard={featureCatalogStandard}
        setFcStandard={setFeatureCatalogStandard}
        featureCatalogId={featureCatalogId}
        setFeatureCatalogId={setFeatureCatalogId}
        coordinateSystemUnits={coordinateSystemUnit}
        setCoordinateSystemUnits={setCoordinateSystemUnit}
        disableEdit={disableEdit}
        configStatus={configStatus}
        projectStatus={projectStatus}
        region={region}
      />
      {quadriProjectId && projectStatus === "Active" && <ProjectSizeInfo quadriProjectId={quadriProjectId} />}
      <ImportedProjectSettings
        connectProjectId={props.connectProjectId}
        unitSystem={unitSystem}
        boundaryCoordinates={boundaryCoordinates}
        csib64={connectProject?.crs?.csib64}
        crsName={crsName}
        tcpsArea={props.tcpsArea}
        tcpsCrsName={props.coordinateSystemName}
        projectEnabled={projectStatus === "Active"}
      />
      {props.connectData.connectProject && (
        <ConnectProjectContext
          region={props.connectData.region}
          description={props.connectData.connectProject.description}
          name={props.connectData.connectProject.name}
          startDate={props.connectData.connectProject.startDate}
          endDate={props.connectData.connectProject.endDate}
        />
      )}
      <QuadriFooter
        connectProjectId={connectProjectId}
        projectStatus={projectStatus}
        changeInProgress={changeInProgress}
      />
    </>
  )
}

export const startBackupTimeoutReload = (t: any, addSnack: (props: SnackProps) => void) => {
  setTimeout(() => {
    console.log(`Warning snackbar since no kafka-notification was received after ${BACKUP_RELOAD_WARNING} ms`)
    addSnack(getBackupReloadWarnSnackProps(t, BACKUP_RELOAD_WARNING))
  }, BACKUP_RELOAD_WARNING)
  setTimeout(() => {
    console.log(`Reloading page since no kafka-notification was received after ${BACKUP_RELOAD_TIMEOUT} ms`)
    window.location.reload()
  }, BACKUP_RELOAD_TIMEOUT)
}

const getBackupReloadWarnSnackProps = (t: any, timeMs: number): SnackProps => ({
  type: "warning",
  message: t("backupReloadWarning.warning")
})

export default ConfigMain
